import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, Check, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STORAGE_KEY = 'xenicos.changesPopup.shownForSession';

// Ventana emergente de alertas críticas al iniciar sesión.
// Reutiliza la colección `notifications` filtrada por los tipos:
//   - alerta_mediacion  → badge "NUEVO EN MEDIACIÓN" (naranja)
//   - alerta_prioridad  → badge "ALTA PRIORIDAD" (rojo)
//   - alerta_revision   → badge "SOLICITADA REVISIÓN" (ámbar)
// Por diseño intencional muestra SOLO estos tipos; el resto de novedades
// (edits normales, etc.) siguen accesibles desde la campanita.
const BADGE_CONFIG = {
  alerta_mediacion: {
    label: 'NUEVO EN MEDIACIÓN',
    className: 'bg-orange-100 text-orange-800 border border-orange-300',
  },
  alerta_prioridad: {
    label: 'ALTA PRIORIDAD',
    className: 'bg-red-100 text-red-800 border border-red-300',
  },
  alerta_revision: {
    label: 'SOLICITADA REVISIÓN',
    className: 'bg-amber-100 text-amber-800 border border-amber-300',
  },
  alerta_revision_resuelta: {
    label: 'REVISIÓN RESUELTA',
    className: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  },
};

const ChangesPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user) return;
    // Se muestra UNA sola vez por sesión de navegador.
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return;
    axios
      .get(`${API}/me/critical-alerts`, { withCredentials: true })
      .then((r) => {
        const list = r.data?.items ?? [];
        setItems(list);
        if (list.length > 0) setOpen(true);
        sessionStorage.setItem(STORAGE_KEY, '1');
      })
      .catch(() => { /* noop */ });
  }, [user]);

  if (!open || items.length === 0) return null;

  const close = () => {
    setOpen(false);
    // Notifica a la campanita para que refresque contadores.
    try { window.dispatchEvent(new CustomEvent('xenicos:refresh-bell')); } catch { /* noop */ }
  };

  // Marca leída una alerta (desaparece del popup y de la campanita).
  const markRead = async (id) => {
    if (!id || busyId) return;
    setBusyId(id);
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setItems((prev) => prev.filter((n) => n.id !== id));
    } finally {
      setBusyId(null);
    }
  };

  // "Revisar más tarde": no marca leída — la alerta queda pendiente y volverá
  // a aparecer en la campanita. Solo la retira del popup actual.
  const reviewLater = (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  // Click en la fila → navega al activo y marca la alerta leída.
  const goToAsset = async (n) => {
    if (n.asset_id) {
      await markRead(n.id);
      navigate(`/activos/${n.asset_id}`);
      close();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={close}
      data-testid="changes-popup-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="changes-popup-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-base">Alertas pendientes</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {items.length} {items.length === 1 ? 'aviso importante' : 'avisos importantes'} sin leer
              </p>
            </div>
          </div>
          <button
            onClick={close}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
            aria-label="Cerrar"
            data-testid="changes-popup-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-3">
          <ul className="space-y-2">
            {items.map((n, idx) => {
              const cfg = BADGE_CONFIG[n.tipo] || {
                label: (n.titulo || 'AVISO').toUpperCase(),
                className: 'bg-slate-100 text-slate-700 border border-slate-300',
              };
              const dir = n.direccion || 'Sin dirección';
              const muni = n.municipio || '';
              return (
                <li
                  key={n.id || idx}
                  className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors"
                  data-testid={`changes-popup-item-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => goToAsset(n)}
                      disabled={!n.asset_id || busyId === n.id}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`changes-popup-asset-${idx}`}
                    >
                      <p className="text-sm font-semibold text-slate-900 truncate">{dir}</p>
                      {muni && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{muni}</p>
                      )}
                      {n.reminder && n.days_pending > 0 && (
                        <p className="text-[11px] text-amber-700 mt-0.5 font-medium">
                          Pendiente hace {n.days_pending} día{n.days_pending === 1 ? '' : 's'}
                        </p>
                      )}
                      {n.note && (
                        <p
                          className="text-[11px] italic mt-1 border-l-2 border-amber-300 pl-2 line-clamp-3"
                          style={{ color: 'var(--color-text-muted)' }}
                          data-testid={`changes-popup-note-${idx}`}
                          title={n.note}
                        >
                          {n.note}
                        </p>
                      )}
                      <span
                        className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-bold tracking-wider rounded ${cfg.className}`}
                        data-testid={`changes-popup-badge-${idx}`}
                      >
                        {cfg.label}
                      </span>
                    </button>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => markRead(n.id)}
                        disabled={busyId === n.id}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded transition-colors disabled:opacity-50"
                        title="Marcar como leída"
                        data-testid={`changes-popup-mark-read-${idx}`}
                      >
                        <Check className="w-3 h-3" /> Leída
                      </button>
                      <button
                        onClick={() => reviewLater(n.id)}
                        disabled={busyId === n.id}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded transition-colors disabled:opacity-50"
                        title="Revisar más tarde — la alerta permanece en la campanita"
                        data-testid={`changes-popup-review-later-${idx}`}
                      >
                        <Clock className="w-3 h-3" /> Más tarde
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[11px] text-slate-400">
            Las alertas sin marcar seguirán disponibles en la campanita.
          </p>
          <button
            onClick={close}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
            data-testid="changes-popup-dismiss"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangesPopup;
